/**
 * Add / edit a marketing contact.
 *
 * Deliberately thinner than the sales ContactSheet: there is no owner, no
 * company record and no pipeline behind these, because a marketing contact is
 * an address to reach rather than somebody a deal is being worked with. The
 * fields here are only what a campaign actually reads — the name it greets, the
 * address it goes to, and the merge fields the copy can use.
 */
import PhoneField from '../PhoneField.jsx';
import { useState } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import { MKT_CONTACT_SOURCES } from '../../constants/marketing.js';
import { digitsOf } from '../../lib/format.js';
import { storedPhoneProblem } from '../../lib/phone.js';
import Sheet, { Group, Field, OptionSelect } from '../Sheet.jsx';

const BLANK = {
  name: '', email: '', phone: '', company: '',
  status: 'Subscribed', source: 'Manual', notes: ''
};

export default function MktContactSheet({ id, onClose }) {
  const { mkt, saveMktContact, removeMarketingRecord, toast } = useApp();

  const existing = id ? (mkt.contacts || []).find(c => c.id === id) : null;
  const [form, setForm] = useState(() => ({ ...BLANK, ...(existing || {}) }));

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = () => {
    const name = String(form.name).trim();
    if (!name) { toast('Name the contact'); return; }
    if (!String(form.email).trim() && !digitsOf(form.phone)) {
      toast('An email address or a phone number — otherwise nothing can reach them');
      return;
    }
    if (digitsOf(form.phone)) {
      const why = storedPhoneProblem(form.phone);
      if (why) { toast(why); return; }
    }

    /* One address belongs to one contact, or a campaign sends twice. */
    const key = String(form.email || '').trim().toLowerCase() || digitsOf(form.phone);
    const clash = (mkt.contacts || []).find(c => c.id !== id
      && (String(c.email || '').trim().toLowerCase() || digitsOf(c.phone)) === key);
    if (clash) { toast(`${clash.name} is already on the list with that address`); return; }

    saveMktContact(id, {
      ...form,
      name,
      email: String(form.email || '').trim(),
      phone: String(form.phone || '').trim()
    });
    onClose();
  };

  const remove = () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove ${form.name || 'this contact'} from the marketing list?`)) return;
    removeMarketingRecord('contacts', id);
    toast('Contact removed');
    onClose();
  };

  return (
    <Sheet
      icon="users"
      tone="g-pink"
      title={id ? 'Edit contact' : 'New contact'}
      onClose={onClose}
      onSave={save}
      saveLabel="Save contact"
      footerLeft={id
        ? <button type="button" className="btn danger" onClick={remove}>Remove</button>
        : null}
    >
      <Group>Contact</Group>

      <Field label="Name">
        <input autoFocus value={form.name || ''} onChange={e => set('name', e.target.value)} />
      </Field>
      <Field label="Company">
        <input value={form.company || ''} onChange={e => set('company', e.target.value)} />
      </Field>
      <Field label="Email">
        <input value={form.email || ''} onChange={e => set('email', e.target.value)} />
      </Field>
      <Field
        label="Phone"
        hint={<div className="fhint">Stored with the country code, which WhatsApp needs.</div>}
      >
        <PhoneField value={form.phone || ''} onChange={v => set('phone', v)} />
      </Field>

      <Group>List</Group>

      {/*
        * Subscription state is not typed in. Nobody adds somebody to a list as
        * "unsubscribed", and offering it here meant a new contact could be
        * saved unreachable by a slip of the mouse. It is set by what actually
        * happens instead: subscribed on arrival, unsubscribed from the row on
        * the contacts page, bounced when a send bounces.
        */}
      <Field label="Source">
        <OptionSelect
          value={form.source}
          onChange={v => set('source', v)}
          options={MKT_CONTACT_SOURCES}
          blank=""
        />
      </Field>
      <Field label="Notes" wide>
        <textarea rows="3" value={form.notes || ''} onChange={e => set('notes', e.target.value)} />
      </Field>
    </Sheet>
  );
}
